import {Component, EventEmitter, HostListener, Input, OnDestroy, Output} from '@angular/core';
import {
    AddressLookupResult,
    AddressLookupService
} from '../../../services/address-lookup/address-lookup.service';

@Component({
    selector: 'ui-address-search',
    templateUrl: './address-search.component.html',
    styles: [':host { display: block; min-width: 0; }']
})
export class AddressSearchComponent implements OnDestroy {
    @Input() inputId = '';
    @Input() name = 'address';
    @Input() label = 'Indirizzo';
    @Input() placeholder = 'Es. Via Roma, Milano';
    @Input() hint = 'Seleziona un suggerimento per compilare civico, CAP, provincia e città.';
    @Input() required = false;
    @Input() value = '';

    @Output() valueChange = new EventEmitter<string>();
    @Output() selected = new EventEmitter<AddressLookupResult>();

    addressResults: AddressLookupResult[] = [];
    addressSearchError = '';
    isSearchingAddress = false;
    isAddressDropdownOpen = false;
    highlightedAddressIndex = 0;

    private searchDebounce: ReturnType<typeof setTimeout> | null = null;
    private searchRequestId = 0;
    private skipNextAddressSearch = false;

    constructor(private addressLookupService: AddressLookupService) {
    }

    onAddressChange(value: string): void {
        this.value = value;
        this.valueChange.emit(value);
        this.addressSearchError = '';

        if (this.skipNextAddressSearch) {
            this.skipNextAddressSearch = false;
            return;
        }

        this.clearSearchDebounce();
        this.searchRequestId++;
        const query = (value ?? '').trim();
        if (query.length < 5) {
            this.addressResults = [];
            this.isAddressDropdownOpen = false;
            this.isSearchingAddress = false;
            return;
        }

        this.isSearchingAddress = true;
        this.isAddressDropdownOpen = true;
        this.searchDebounce = setTimeout(() => {
            void this.searchAddress();
        }, 350);
    }

    onAddressFocus(): void {
        if (this.addressResults.length > 0 || this.addressSearchError) {
            this.isAddressDropdownOpen = true;
        }
    }

    onAddressKeydown(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.highlightedAddressIndex = Math.min(
                this.highlightedAddressIndex + 1,
                Math.max(this.addressResults.length - 1, 0)
            );
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.highlightedAddressIndex = Math.max(this.highlightedAddressIndex - 1, 0);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const selected = this.addressResults[this.highlightedAddressIndex];
            if (selected) {
                this.selectAddress(selected);
            } else {
                void this.searchAddress();
            }
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.closeAddressDropdown();
        }
    }

    async searchAddress(): Promise<void> {
        const query = (this.value ?? '').trim();
        this.clearSearchDebounce();
        if (query.length < 5) {
            this.addressResults = [];
            this.isAddressDropdownOpen = false;
            this.isSearchingAddress = false;
            return;
        }

        const requestId = ++this.searchRequestId;
        this.isSearchingAddress = true;
        this.isAddressDropdownOpen = true;

        try {
            const results = await this.addressLookupService.searchItalianAddress(query);
            if (requestId !== this.searchRequestId) {
                return;
            }
            this.addressResults = results;
            this.highlightedAddressIndex = 0;
            this.addressSearchError = results.length === 0
                ? 'Nessun indirizzo trovato. Prova ad aggiungere città o CAP.'
                : '';
        } catch {
            if (requestId !== this.searchRequestId) {
                return;
            }
            this.addressResults = [];
            this.addressSearchError = 'La ricerca indirizzo non è disponibile. Puoi compilare i campi manualmente.';
        } finally {
            if (requestId === this.searchRequestId) {
                this.isSearchingAddress = false;
            }
        }
    }

    selectAddress(result: AddressLookupResult): void {
        const address = result.address ?? {};
        const street = address.road ?? address.pedestrian ?? address.footway;
        const nextValue = street || result.display_name.split(',')[0];

        this.skipNextAddressSearch = true;
        this.value = nextValue;
        this.valueChange.emit(nextValue);
        this.selected.emit(result);
        this.addressResults = [];
        this.addressSearchError = '';
        this.closeAddressDropdown();
    }

    getAddressTitle(result: AddressLookupResult): string {
        const address = result.address ?? {};
        const street = [address.road ?? address.pedestrian ?? address.footway, address.house_number]
            .filter(Boolean)
            .join(' ');
        return street || result.display_name.split(',')[0];
    }

    getAddressSubtitle(result: AddressLookupResult): string {
        const address = result.address ?? {};
        const city = address.city ?? address.town ?? address.village ?? address.municipality;
        return [city, address.postcode].filter(Boolean).join(' · ');
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('[data-address-autocomplete]')) {
            this.closeAddressDropdown();
        }
    }

    ngOnDestroy(): void {
        this.clearSearchDebounce();
    }

    private closeAddressDropdown(): void {
        this.isAddressDropdownOpen = false;
        this.highlightedAddressIndex = 0;
    }

    private clearSearchDebounce(): void {
        if (this.searchDebounce) {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = null;
        }
    }
}
